import type { Key } from 'react';
import React, { useCallback, useState } from 'react';
import type { VirtualItem } from '@tanstack/react-virtual';
import type { TableRowSelection } from 'antd/es/table/interface';

import { EmptyText } from '../EmptyText';
import { translate } from '@chaika/i18n/helper';
import type { MadeColumnType } from '../hooks/useMakeColumns';
import type { RowHandlers, VirtualTableExpandable } from './types';
import { BodyRow } from './BodyRow';
import { EmptyBodyPlaceholder } from '@components/Table/VirtualTable/EmptyBodyPlaceholder';

interface TableBodyProps {
    dataSource: readonly any[];
    loading: boolean;
    emptyText?: string;
    scrollRef: React.RefObject<HTMLDivElement | null>;
    maxHeight: number;
    syncHeaderScroll: () => void;
    totalSize: number;
    virtualItems: VirtualItem[];
    getRecordKey: (record: any) => string;
    measureElement: (node: Element | null) => void;
    displayColumns: MadeColumnType[];
    onRow?: (record: any) => RowHandlers;
    rowClassName?: string | ((record: any, index: number, indent: number) => string);
    rowSelection?: TableRowSelection<any>;
    expandable?: VirtualTableExpandable;
}

export function TableBody({
    dataSource,
    loading,
    emptyText,
    scrollRef,
    maxHeight,
    syncHeaderScroll,
    totalSize,
    virtualItems,
    getRecordKey,
    measureElement,
    displayColumns,
    onRow,
    rowClassName,
    rowSelection,
    expandable,
}: TableBodyProps) {
    const [internalExpandedKeys, setInternalExpandedKeys] = useState<Key[]>([]);

    const isExpandableControlled = expandable && expandable.expandedRowKeys !== undefined;
    const expandedKeys = isExpandableControlled ? (expandable.expandedRowKeys ?? []) : internalExpandedKeys;
    const setExpandedKeys = useCallback(
        (updater: Key[] | ((prev: Key[]) => Key[])) => {
            if (!isExpandableControlled) {
                setInternalExpandedKeys(updater instanceof Function ? updater(internalExpandedKeys) : updater);
            }
        },
        [isExpandableControlled, internalExpandedKeys],
    );

    const isEmpty = dataSource.length === 0 && !loading;

    return (
        <div
            ref={scrollRef}
            className={'virtual_table_scroll'}
            style={{ maxHeight: maxHeight }}
            onScroll={syncHeaderScroll}
        >
            {isEmpty ? (
                <>
                    <EmptyBodyPlaceholder displayColumns={displayColumns} />
                    <div className="virtual_table_empty_text_overlay">
                        <EmptyText text={emptyText ?? translate('nothingFoundTryChangeRequest')} />
                    </div>
                </>
            ) : (
                <div
                    className="virtual_table_body"
                    style={{ height: `${totalSize}px` }}
                >
                    <div
                        className="virtual_table_rows_container"
                        style={{
                            top: `${virtualItems[0]?.start ?? 0}px`,
                        }}
                    >
                        {virtualItems.map(virtualRow => {
                            const record = dataSource[virtualRow.index];
                            if (!record) return null;
                            const key = getRecordKey(record);
                            return (
                                <BodyRow
                                    key={key}
                                    recordKey={key}
                                    record={record}
                                    index={virtualRow.index}
                                    measureElement={measureElement}
                                    displayColumns={displayColumns}
                                    onRow={onRow}
                                    rowClassName={rowClassName}
                                    rowSelection={rowSelection}
                                    expandable={expandable}
                                    expandedKeys={expandedKeys}
                                    setExpandedKeys={setExpandedKeys}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
